import type { ComponentType, ReactNode } from "react";
import VideoPlayerSideBar from "../VideoPlayerSideBar";
import { AudienceLayerPanel } from "../../../components/AudienceLayerPanel";
import type { AudienceLayerHostContext } from "./useAudienceLayerHost";

// The sidebar destructures its props inline without declaring a type, so it
// has no props signature to spread into. Naming that here keeps the cast in
// one place instead of at each use.
const LegacySidebar = VideoPlayerSideBar as unknown as ComponentType<Record<string, unknown>>;

type Props = {
  audienceLayerHost: AudienceLayerHostContext | null;
  // What to show over the panel while a phone owns the Audience Layer for
  // this game. Named here and pulled out explicitly, same as the two props
  // above: this component spreads everything it does not recognize straight
  // through to the legacy sidebar, so an unnamed prop would land there as a
  // stray DOM attribute instead of reaching AudienceLayerPanel.
  blocked?: ReactNode;
  // Everything else goes straight through to the sidebar this may replace.
  // Deliberately loose: that component takes upwards of forty untyped props,
  // and re-declaring them here would be a second list to keep in step with
  // the first for no benefit, since this component reads none of them.
  [key: string]: unknown;
};

/**
 * Chooses which sidebar the Watch page shows, so the three places that render
 * one do not each carry the same conditional around forty-odd props.
 *
 * The Audience Layer wins wherever somebody is signed in. Signed out, and if
 * the remote fails to load at all, the existing sidebar renders exactly as
 * before: this replaces the sidebar, so a remote that cannot load must not take
 * the chat down with it.
 */
export function WatchSidebar({
  audienceLayerHost,
  blocked,
  ...sidebarProps
}: Props) {
  return (
    <AudienceLayerPanel
      hostContext={audienceLayerHost}
      blocked={blocked}
      fallback={<LegacySidebar {...sidebarProps} />}
    />
  );
}
